import React from "react";
import styled from "styled-components/macro";
import { Link as RouterLink } from "react-router-dom";
import { Input, TextLink, Button, Typography } from "@deeptrue-frontend/ui";
import { Formik, Field as FormikField } from "formik";
import * as Yup from "yup";

import RouterProvider from "components/RouterProvider/RouterProvider";
import { useFormatMessage } from "components/IntlProvider";
import { FormatMessage } from "components/IntlProvider/hooks/hooks";

interface FormValues {
  email: string;
  password: string;
}

export type SubmitHandler = (values: FormValues) => void;

const StyledForm = styled.form`
  width: 100%;
  margin-top: 1rem;
`;

const LabelWrapper = styled.span`
  display: flex;
  justify-content: space-between;
`;

const getValidationSchema = (t: FormatMessage) =>
  Yup.object().shape<FormValues>({
    email: Yup.string()
      .email(t("errors.fields.email.format", "Invalid email format"))
      .required(t("errors.fields.required", "Field is required")),
    password: Yup.string().required(
      t("errors.fields.required", "Field is required")
    ),
  });

interface Props {
  onSubmit: SubmitHandler;
  initialValues?: FormValues;
  submitError?: string;
  isSubmitting: boolean;
}
const SignInForm = ({
  onSubmit,
  isSubmitting,
  submitError,
  initialValues = { email: "", password: "" },
}: Props) => {
  const t = useFormatMessage();

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={initialValues}
      validationSchema={getValidationSchema(t)}
    >
      {({ handleSubmit, errors, touched }) => (
        <StyledForm onSubmit={handleSubmit}>
          <FormikField
            id="email"
            name="email"
            as={Input}
            hasError={touched.email && errors.email}
            helperText={touched.email && errors.email}
            label={t("forms.forgotpassword.email", "Email Address")}
            tabIndex={1}
          />
          <FormikField
            id="password"
            name="password"
            type="password"
            hasError={touched.password && errors.password}
            helperText={touched.password && errors.password}
            as={Input}
            tabIndex={2}
            label={
              <LabelWrapper>
                {t("common.password", "Password")}
                <RouterLink
                  to={RouterProvider.ROUTES.RECOVER_PASSWORD}
                  tabIndex={3}
                >
                  <TextLink>
                    {t("forms.signin.forgotpassword", "Forgot password?")}
                  </TextLink>
                </RouterLink>
              </LabelWrapper>
            }
          />
          {submitError && (
            <Typography variant="copy3" color="punch">
              {submitError}
            </Typography>
          )}
          <Button
            mt="1rem"
            width="100%"
            disabled={isSubmitting}
            isLoading={isSubmitting}
            type="submit"
          >
            {t("forms.signin.signinbutton", "Sign In")}
          </Button>
        </StyledForm>
      )}
    </Formik>
  );
};

export default SignInForm;
